import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, PageHero, SectionHead } from "@/components/shared";
import { CTABand } from "@/pages/Home";
import { API } from "@/lib/api";
import { PACKAGES, FAQS, IMAGES } from "@/lib/content";

export default function Services() {
  const [prices, setPrices] = useState({});

  useEffect(() => {
    API.get("/pricing")
      .then(({ data }) => {
        const map = {};
        data.forEach((p) => { map[p.package] = p; });
        setPrices(map);
      })
      .catch(() => {});
  }, []);

  return (
    <main data-testid="services-page">
      <PageHero
        eyebrow="Services & Packages"
        title="Transforming island ambition into digital market leadership."
        lead="Clear scopes, honest timelines, fixed outcomes. Choose a package or let us shape one around your season."
        image={IMAGES.yacht}
      />

      <section className="pb-24 lg:pb-32" data-testid="packages-matrix">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 md:grid-cols-2">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 0.1}>
                <div className="gold-ring-card group flex h-full flex-col p-8 lg:p-10" data-testid={`service-card-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-glow">
                      {p.tag}
                    </span>
                    <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-ivory sm:text-3xl">{p.name}</h3>
                  {prices[p.name] && (
                    <div className="mt-3" data-testid={`service-price-${p.id}`}>
                      <p className="font-mono text-lg font-medium text-gold-glow">{prices[p.name].price}</p>
                      {prices[p.name].note && <p className="mt-1 text-xs text-slate-500">{prices[p.name].note}</p>}
                    </div>
                  )}
                  <p className="mt-4 text-sm font-light leading-relaxed text-slate-400 sm:text-base">{p.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {pt}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-gold/10 pt-6">
                    <Link
                      to={`/services/${p.id}`}
                      data-testid={`service-scope-${p.id}`}
                      className="inline-flex items-center gap-2 text-sm font-medium text-ivory transition-colors duration-300 hover:text-gold-glow"
                    >
                      View full scope <ArrowRight className="h-4 w-4" />
                    </Link>
                    <a
                      href="/contact"
                      data-testid={`service-enquire-${p.id}`}
                      className="inline-flex items-center gap-2 text-sm font-medium text-gold-glow transition-colors duration-300 hover:text-gold-light"
                    >
                      Enquire <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-gold/10 bg-navy-deep/50 py-24 lg:py-32" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <SectionHead
            eyebrow="Questions, answered"
            title="The things owners ask us first"
            align="center"
          />
          <Reveal delay={0.15} className="mt-12">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-2xl border border-gold/20 bg-navy-surface px-6 data-[state=open]:border-gold/40"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="py-5 text-left font-serif text-lg text-ivory hover:text-gold-glow hover:no-underline" data-testid={`faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm font-light leading-relaxed text-slate-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Not sure which package fits?"
        text="Book a free thirty-minute consultation. We will tell you honestly what you need — and what you don't."
      />
    </main>
  );
}
